import { parseKstDateTime } from '../components/seedingDetail/adapters/seedingViewCommon';

/**
 * 시딩 제안/신청 "신규 미확인" blue dot 판정용 watermark.
 *
 * 본 최신 1건의 `participationId` + `createdAt` 를 통째로 저장하고, 다음 진입의 최신 1건과
 * 비교한다.
 *
 * NOTE(QAT-39429 재발): createdAt 비교는 **문자열 `>=` 가 아니라 `parseKstDateTime` 로 ms
 * 정규화한 뒤** 한다. 초기 구현은 "양변 모두 BE 값(공백+KST)이라 동형"이라 가정해 raw 문자열
 * 비교를 썼는데, BE 가 `createdAt` 구분자를 공백→`T` 로 변경하면서 **구 watermark(공백) vs 신
 * API(`T`)** 이종 비교로 결함이 재발했다(`'T'`(84) > `' '`(32) 라 같은 날 더 이른 건이 "최신"
 * 으로 오판 → 확인 후에도 dot 잔존). ms 정규화가 분리자(T/공백) 차이와 iOS `new Date(공백)` NaN
 * 을 함께 흡수한다. (QAT-39429 / SID-4312 / SID-4422, 메모리 reference_curator_be_datetime_kst_compare)
 */
export type SeedingUnviewedWatermark = { id: number; createdAt: string };

/** localStorage 에서 watermark 를 읽는다. 구(舊) ISO 문자열 등 비-JSON 값은 null(=미확인) 취급. */
export const readSeedingWatermark = (storageKey: string): SeedingUnviewedWatermark | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SeedingUnviewedWatermark>;
    if (typeof parsed?.id === 'number' && typeof parsed?.createdAt === 'string') {
      return { id: parsed.id, createdAt: parsed.createdAt };
    }
  } catch {}
  return null;
};

export interface SeedingUnviewedLatest {
  participationId: number;
  createdAt: string;
}

/**
 * 최신 PENDING 1건(latest)과 저장된 watermark(seen)로 dot 노출 여부를 판정한다.
 *
 * - latest 없음(0건) → 미노출.
 * - 진입 이력 없음(seen 없음) + 1건 이상 → 노출.
 * - 본 최신과 **다른 제안/신청(id)** 이고 **그 이후(createdAt)** → 신규 → 노출.
 *   - createdAt 는 `parseKstDateTime` ms `>=` 비교: "본 최신 소진 → 옛 건이 top 부상"
 *     false-positive 는 id 가 막고, "같은 초(second) 신규" strict `>` 누락은 id 가 보강한다.
 *     (`>=` 의미 보존이 필수 — `isAfter` 같은 strict `>` 로 바꾸면 같은 초 신규가 누락된다.)
 */
export const isSeedingItemUnviewed = (
  latest: SeedingUnviewedLatest | null,
  seen: SeedingUnviewedWatermark | null,
): boolean => {
  if (latest === null) return false;
  if (seen === null) return true;
  return (
    latest.participationId !== seen.id &&
    parseKstDateTime(latest.createdAt) >= parseKstDateTime(seen.createdAt)
  );
};
